import { useTranslation } from "react-i18next";
import Link from "@/components/feature/LocaleLink";
import logo from "@/assets/images/logo.png";

// to: 홈 섹션은 "/#section", 서브페이지는 "/path" 또는 "/path#section"
type FooterLink = { key: string; to: string };

// 상단 메뉴와 같은 구성(회사소개 · 서비스 · 솔루션 · 위성통신 · 고객지원), 그룹마다 4개씩
const GROUPS: { title: FooterLink; links: FooterLink[] }[] = [
  {
    title: { key: "footer.g1.title", to: "/about" },
    links: [
      { key: "footer.g1.l1", to: "/about#overview" },
      { key: "footer.g1.l2", to: "/about#organization" },
      { key: "footer.g1.l3", to: "/about#performance" },
      { key: "footer.g1.l4", to: "/about#history" },
    ],
  },
  {
    title: { key: "footer.g2.title", to: "/services" },
    links: [
      { key: "footer.g2.l1", to: "/services#block-1" },
      { key: "footer.g2.l2", to: "/services#block-2" },
      { key: "footer.g2.l3", to: "/services#block-3" },
      { key: "footer.g2.l4", to: "/services#scope" },
    ],
  },
  {
    title: { key: "footer.g3.title", to: "/solutions" },
    links: [
      { key: "footer.g3.l1", to: "/solutions/firewall" },
      { key: "footer.g3.l2", to: "/solutions/usb-security" },
      { key: "footer.g3.l3", to: "/solutions/rmm" },
      { key: "footer.g3.l4", to: "/solutions/nms" },
    ],
  },
  {
    title: { key: "footer.g4.title", to: "/satellite" },
    links: [
      { key: "footer.g4.l1", to: "/satellite#systems" },
      { key: "footer.g4.l2", to: "/satellite#systems" },
      { key: "footer.g4.l3", to: "/solutions/citadel" },
      { key: "footer.g4.l4", to: "/satellite#scope" },
    ],
  },
  {
    title: { key: "footer.g5.title", to: "/services#process" },
    links: [
      { key: "footer.g5.l1", to: "/#contact" },
      { key: "footer.g5.l2", to: "/careers" },
      { key: "footer.g5.l3", to: "/downloads" },
      { key: "footer.g5.l4", to: "/notice" },
    ],
  },
];

export default function Footer() {
  const { t } = useTranslation();

  return (
    // 데이터플레어처럼 밝은 회색 푸터 + 검정 글자
    <footer className="bg-background-200 text-foreground-700 shadow-[0_-8px_30px_-20px_rgba(0,0,0,0.3)]">
      <div className="max-w-7xl mx-auto px-4 md:px-10 pt-16 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <Link to="/" className="inline-flex flex-col gap-2 cursor-pointer mb-6">
              <img src={logo} alt="오션커넥트 OCEAN CONNECT" loading="lazy" decoding="async" className="h-10 w-auto self-start" />
              <span className="text-[10px] tracking-[0.2em] text-foreground-500 font-label">
                VESSEL IT · INTEGRATED MANAGEMENT
              </span>
            </Link>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-sm">
              {t("footer.desc")}
            </p>
          </div>

          {/* 링크 그룹: 같은 너비의 열 5개, 각 4개 항목 */}
          <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-5 gap-6">
            {GROUPS.map((g) => (
              <div key={g.title.key}>
                <h4 className="font-heading font-bold text-foreground-950 mb-4 text-sm tracking-wider uppercase">
                  <Link to={g.title.to} className="cursor-pointer hover:text-primary-600">
                    {t(g.title.key)}
                  </Link>
                </h4>
                <ul className="space-y-2">
                  {g.links.map((lk) => (
                    <li key={lk.key}>
                      <Link
                        to={lk.to}
                        className="text-sm text-foreground-600 hover:text-primary-600 cursor-pointer"
                      >
                        {t(lk.key)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-400/60">
          <div className="flex flex-col md:flex-row justify-between gap-4 text-xs text-foreground-600">
            <div className="space-y-1">
              <div>
                <span className="text-foreground-950 font-semibold mr-2">
                  {t("footer.company")}
                </span>
                {t("footer.ceo")}
              </div>
              <div className="flex flex-wrap gap-x-2">
                <a href="tel:+82517150611" className="hover:text-primary-600 cursor-pointer">
                  {t("footer.tel")}
                </a>
                <span>·</span>
                <span>{t("footer.fax")}</span>
                <span>·</span>
                <a href="mailto:info@oceanconnect.co.kr" className="hover:text-primary-600 cursor-pointer">
                  info@oceanconnect.co.kr
                </a>
              </div>
              <div>
                <a href="tel:+827075880611" className="hover:text-primary-600 cursor-pointer">
                  {t("footer.helpdesk")}
                </a>
              </div>
            </div>
            <div className="flex flex-col md:items-end gap-2">
              <div className="flex gap-4">
                <Link to="/privacy" className="hover:text-primary-600 cursor-pointer">
                  {t("footer.privacy")}
                </Link>
                <Link to="/terms" className="hover:text-primary-600 cursor-pointer">
                  {t("footer.terms")}
                </Link>
              </div>
              <div>{t("footer.copyright", { year: new Date().getFullYear() })}</div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
